import React from 'react';
import noop from 'lodash/noop';
import union from 'lodash/union';
import { withFlagService } from '@atlassian/jira-flags';
import {
	fireTrackAnalytics,
	ContextualAnalyticsData,
	DROPDOWN,
} from '@atlassian/jira-product-analytics-bridge';
import { TRACK_EVENT_ACTION } from '../../../../common/constants';
import type { RoleId, RoleTypeId, ActorType } from '../../../../common/types';
import RolesPicker from '../../../../common/ui/roles-picker';
import {
	ANALYTICS_ROLE_OPTION,
	ANALYTICS_ROLE_PICKER,
} from '../../../../common/ui/roles-picker/constants';
import type {
	AddActorToRole as ActorRoleType,
	RemoveActorFromRole as RemoveActorType,
} from '../../../../common/ui/roles-picker/types';
import { getActorRoleIds } from '../../../../common/utils';
import AddActorsToRoleDI from '../../../../services/add-actors-to-role';
import RemoveActorFromRoleDI from '../../../../services/remove-actor-from-role';
import { ANALYTICS_ROOT_KEY, ANALYTICS_ROLE_ADDED, ANALYTICS_ROLE_REMOVED } from './constants';
import messages from './messages';
import type { Props } from './types';

export const RolesPickerContainer = (props: Props) => {
	let removeActorFromRoleService = noop;
	let addActorFromRoleService = noop;

	const {
		actor,
		roles,
		onRolesUpdated,
		AddActorsToRole,
		RemoveActorFromRole,
		flagService: { showFlag },
	} = props;
	// when a role is selected from the roles picker
	const onAddActorToRole: ActorRoleType = ({ addedRoleId, roleTypeId, type, analyticsEvent }) => {
		// add addedRoleId to actor.roles array
		onRolesUpdated([...getActorRoleIds(actor), addedRoleId], roleTypeId);
		const addActorService = addActorFromRoleService({ roleTypeId, addedRoleId, type });
		// @ts-expect-error - TS1345 - An expression of type 'void' cannot be tested for truthiness.
		if (addActorService) {
			// @ts-expect-error - TS2339 - Property 'then' does not exist on type 'never'.
			return addActorService.then(() => {
				fireTrackAnalytics(analyticsEvent, TRACK_EVENT_ACTION, ANALYTICS_ROLE_OPTION, {
					roleId: addedRoleId,
					type: ANALYTICS_ROLE_ADDED,
				});
			});
		}
		return undefined;
	};

	// when a role is unchecked from the roles picker
	const onRemoveActorFromRole: RemoveActorType = ({
		roleTypeId,
		removedRoleId,
		type,
		analyticsEvent,
	}) => {
		// remove removedRoleId from actor.roles array
		onRolesUpdated(
			getActorRoleIds(actor).filter((roleId) => roleId !== removedRoleId), // remove removedRoleRoleId from actor.roles array
			roleTypeId,
		);
		const removeActorService = removeActorFromRoleService({
			roleTypeId,
			roleId: removedRoleId,
			type,
		});
		// @ts-expect-error - TS1345 - An expression of type 'void' cannot be tested for truthiness.
		if (removeActorService) {
			// @ts-expect-error - TS2339 - Property 'then' does not exist on type 'never'.
			return removeActorService.then(() => {
				fireTrackAnalytics(analyticsEvent, TRACK_EVENT_ACTION, ANALYTICS_ROLE_OPTION, {
					roleId: removedRoleId,
					type: ANALYTICS_ROLE_REMOVED,
				});
			});
		}
		return undefined;
	};

	const onRemoveActorFromRoleError = (
		removedRoleId: RoleId,
		roleTypeId: RoleTypeId,
		type: ActorType,
		isLastAdminError: boolean,
	) => {
		// Union to remove duplicate
		onRolesUpdated(union([...getActorRoleIds(actor), removedRoleId]), roleTypeId);
		showFlag({
			type: 'error',
			title: messages.genericErrorTitle,
			description: isLastAdminError ? messages.lastAdminErrorMessage : messages.genericErrorMsg,
			testId:
				'users-and-roles.ui.actors-table.role-cell.roles-picker-container.remove-actor-from-role.error',
		});
	};

	const onAddActorToRoleError = (addedRoleId: RoleId, roleTypeId: RoleTypeId) => {
		onRolesUpdated(
			getActorRoleIds(actor).filter((roleId) => roleId !== addedRoleId), // remove removedRoleRoleId from actor.roles array
			roleTypeId,
		);
		showFlag({
			type: 'error',
			title: messages.genericErrorTitle,
			description: messages.genericErrorMsg,
			testId:
				'users-and-roles.ui.actors-table.role-cell.roles-picker-container.add-actor-to-role.error',
		});
	};

	return (
		<ContextualAnalyticsData sourceType={DROPDOWN} sourceName={ANALYTICS_ROLE_PICKER}>
			<AddActorsToRole onAddActorToRoleError={onAddActorToRoleError}>
				{({ onAddActorToRole: addActorToRole }) => {
					addActorFromRoleService = addActorToRole;
					return (
						<RemoveActorFromRole onRemoveActorFromRoleError={onRemoveActorFromRoleError}>
							{({ onRemoveActorFromRole: removeActorFromRole }) => {
								removeActorFromRoleService = removeActorFromRole;
								return (
									<RolesPicker
										addActorToRole={onAddActorToRole}
										removeActorFromRole={onRemoveActorFromRole}
										shouldFitContainer
										actor={actor}
										roles={roles}
										analyticsKey={ANALYTICS_ROOT_KEY}
									/>
								);
							}}
						</RemoveActorFromRole>
					);
				}}
			</AddActorsToRole>
		</ContextualAnalyticsData>
	);
};

RolesPickerContainer.defaultProps = {
	AddActorsToRole: AddActorsToRoleDI,
	RemoveActorFromRole: RemoveActorFromRoleDI,
};

export default withFlagService(RolesPickerContainer);
